import React from 'react';
import styled from 'styled-components';
import {useIntl, FormattedMessage} from 'react-intl';
import {FlexColumn} from '@forecast-it/design-system';
import {D} from './design-tokens';

// ── Suggestion message IDs ────────────────────────────────────────────────────

const SUGGESTION_IDS = [
	'forecast_agent.suggestion.portfolio_health',
	'forecast_agent.suggestion.team_utilisation',
	'forecast_agent.suggestion.projects_at_risk',
	'forecast_agent.suggestion.capacity_next_week',
] as const;

// ── Styled components ─────────────────────────────────────────────────────────

const Wrapper = styled(FlexColumn)`
	padding: 20px 8px 12px;
`;

const Greeting = styled.div`
	color: ${D.textPrimary};
	font-weight: 600;
	font-size: 14px;
	margin-bottom: 4px;
`;

const Subtitle = styled.div`
	color: ${D.textSecondary};
	font-size: 12px;
	margin-bottom: 16px;
	line-height: 1.6;
`;

const SuggestionsLabel = styled.div`
	font-size: 11px;
	font-weight: 600;
	color: ${D.textMuted};
	letter-spacing: 0.4px;
	text-transform: uppercase;
	margin-bottom: 8px;
`;

const SuggestionsList = styled(FlexColumn)`
	gap: 6px;
`;

const SuggestionButton = styled.button`
	padding: 9px 12px;
	background: ${D.bg};
	text-align: left;
	border: 1px solid ${D.border};
	border-radius: 8px;
	font-size: 12px;
	color: ${D.textSecondary};
	cursor: pointer;
	line-height: 1.4;
	transition: border-color 0.15s, background 0.15s;

	&:hover {
		border-color: ${D.accentMid};
		background: ${D.accentHover};
	}
`;

// ── Component ─────────────────────────────────────────────────────────────────

type EmptyStateProps = {
	onSuggestionClick: (text: string) => void;
};

export function EmptyState({onSuggestionClick}: EmptyStateProps): JSX.Element {
	const {formatMessage} = useIntl();

	return (
		<Wrapper>
			<Greeting>
				<FormattedMessage id="forecast_agent.empty_state.greeting" />
			</Greeting>
			<Subtitle>
				<FormattedMessage id="forecast_agent.empty_state.subtitle" />
			</Subtitle>
			<SuggestionsLabel>
				<FormattedMessage id="forecast_agent.empty_state.suggestions_label" />
			</SuggestionsLabel>
			<SuggestionsList>
				{SUGGESTION_IDS.map(id => {
					const text = formatMessage({id});
					return (
						<SuggestionButton type="button" key={id} onClick={() => onSuggestionClick(text)}>
							{text}
						</SuggestionButton>
					);
				})}
			</SuggestionsList>
		</Wrapper>
	);
}
